<template>
  <section class="hero is-default is-bold">
    <div class="hero-body">
      <div class="container">
        <my-notification :icon="'info'">
          <template #title>
            {{ $t('viewEcoLabware.title-1') }}
          </template>
          <span v-html="$t('viewEcoLabware.introduction')" />
        </my-notification>
        <br />
        <div class="columns">
          <div class="column">
            <b-notification style="margin-bottom: 1rem" :closable="false">
              <b-field
                :label="$t('viewEcoLabware.labwareName') + ' *'"
                :type="{ 'is-danger': errors.has('labwareName') }"
                :message="errors.first('labwareName')"
              >
                <b-select
                  expanded
                  v-validate="'required'"
                  :placeholder="$t('viewEcoLabware.labwareName') + ' *'"
                  name="labwareName"
                  v-model="labwareName"
                >
                  <option
                    :value="name"
                    v-for="(name, index) in NAMES"
                    :key="index"
                  >
                    {{ $t('viewEcoLabware.' + name) }}
                  </option>
                </b-select>
              </b-field>
              <b-field
                :label="$t('viewEcoLabware.country') + ' *'"
                :type="{ 'is-danger': errors.has('country') }"
                :message="errors.first('country')"
              >
                <b-autocomplete
                  v-model="selectedCountry"
                  :open-on-focus="true"
                  expanded
                  @select="(option) => setCountry(option)"
                  :data="filteredCountries"
                  :field="langKey"
                  :clearable="true"
                >
                  <template slot-scope="props">
                    <div class="media">
                      <div class="media-left">
                        <flag :iso="props.option.flag" />
                      </div>
                      <div class="media-content">
                        <strong>{{ props.option[langKey] }}</strong>
                      </div>
                    </div>
                  </template>
                </b-autocomplete>
                <p class="control">
                  <span expanded class="button is-static">
                    <flag v-if="glass.country" :iso="glass.country" />
                    <i v-else class="fa fa-flag" aria-hidden="true"></i>
                  </span>
                </p>
              </b-field>
            </b-notification>
            <div class="columns">
              <div class="column">
                <plastic-form v-model="plastic" />
              </div>
              <div class="column">
                <glass-form v-model="glass" />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="columns">
              <div class="column">
                <b-notification
                  :closable="false"
                  style="height: 110px; padding-top: 30px !important"
                >
                  <div class="level-item has-text-centered">
                    <div>
                      <p class="heading has-text-distance">
                        {{ $t('viewEcoLabware.label-2') }}
                      </p>
                      <div class="title is-size-4 has-text-distance">
                        <p>
                          {{ Math.round(plasticIntensity * 10000) / 10 }}
                          <span class="is-size-6">{{ unit }}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </b-notification>
              </div>
              <div class="column">
                <b-notification
                  :closable="false"
                  style="height: 110px; padding-top: 30px !important"
                >
                  <div class="level-item has-text-centered">
                    <div>
                      <p class="heading has-text-distance">
                        {{ $t('viewEcoLabware.label-1') }}
                      </p>
                      <div class="title is-size-4 has-text-distance">
                        <p>
                          {{ Math.round(glassIntensity * 10000) / 10 }}
                          <span class="is-size-6">{{ unit }}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </b-notification>
              </div>
              <div class="column">
                <b-notification
                  :closable="false"
                  style="height: 110px; padding-top: 30px !important"
                >
                  <div class="level-item has-text-centered">
                    <div>
                      <p class="heading has-text-distance">
                        {{ $t('viewEcoLabware.label-3') }}
                      </p>
                      <div class="title is-size-4 has-text-distance">
                        <p v-if="reductionRate <= 0">
                          - {{ Math.round(-reductionRate * 10) / 10 }}
                          <span class="is-size-6">%</span>
                        </p>
                        <p v-else>
                          + {{ Math.round(reductionRate * 10) / 10 }}
                          <span class="is-size-6">%</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </b-notification>
              </div>
            </div>
            <b-notification
              :style="{ padding: '7px 0rem 7px 1.5rem' }"
              :closable="false"
            >
              <nav class="level">
                <div class="level-left">
                  <div class="panel-block">
                    <b-tooltip
                      :label="$t('viewEcoLabware.tooltip-3')"
                      :append-to-body="true"
                      type="is-black"
                      multilined
                    >
                      <b-button
                        @click="impact = 'carbon'"
                        :type="impact === 'carbon' ? 'is-primary' : ''"
                        size="is-small"
                        icon-pack="icomoon"
                        class="button"
                        icon-left="carbon"
                      />
                    </b-tooltip>
                    <b-tooltip
                      :label="$t('viewEcoLabware.tooltip-4')"
                      :append-to-body="true"
                      type="is-black"
                      multilined
                    >
                      <b-button
                        @click="impact = 'water'"
                        :type="impact === 'water' ? 'is-primary' : ''"
                        size="is-small"
                        class="button"
                        icon-left="tint"
                      />
                    </b-tooltip>
                  </div>
                </div>
                <div class="level-right">
                  <div class="panel-block">
                    <b-tooltip
                      :label="$t('viewEcoLabware.tooltip-1')"
                      :append-to-body="true"
                      type="is-black"
                      multilined
                    >
                      <b-button
                        @click="stacking = 'normal'"
                        :type="stacking === 'normal' ? 'is-primary' : ''"
                        size="is-small"
                        class="button"
                        icon-left="bar-chart"
                      />
                    </b-tooltip>
                    <b-tooltip
                      :label="$t('viewEcoLabware.tooltip-2')"
                      :append-to-body="true"
                      type="is-black"
                      multilined
                    >
                      <b-button
                        @click="stacking = 'percent'"
                        :type="stacking === 'percent' ? 'is-primary' : ''"
                        size="is-small"
                        class="button"
                      >
                        <strong>%</strong>
                      </b-button>
                    </b-tooltip>
                  </div>
                </div>
              </nav>
            </b-notification>
            <plasticvsglass-bar
              style="margin-top: 50px"
              :glass="glass"
              :plastic="plastic"
              :stacking="stacking"
              :impact="impact"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { GlassLabWare, PlasticLabWare } from '@/models/plastic/LabWare.js'
import LABWARES_FACTORS from '@/../data/factors/lca/labWaresFactors.json'

export default {
  name: 'EcoLabware',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'glass-form': () => import('@/components/ecolabware/GlassForm.vue'),
    'plastic-form': () => import('@/components/ecolabware/PlasticForm.vue'),
    'plasticvsglass-bar': () =>
      import('@/components/graphics/PlasticVsGlassBar.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  data () {
    return {
      labwareName: null,
      stacking: 'normal',
      impact: 'carbon',
      plastic: new PlasticLabWare(),
      glass: new GlassLabWare(),
      NAMES: Object.keys(GlassLabWare.DEFAULT_CONFIGURATION),
      WEIGHTS: {
        'tube.10': {
          glass: 10,
          plastic: 4
        },
        'tube.20': {
          glass: 20,
          plastic: 7
        },
        'tube.30': {
          glass: 26,
          plastic: 16
        },
        'erlenmeyer.100': {
          glass: 65,
          plastic: 60
        },
        'erlenmeyer.250': {
          glass: 140,
          plastic: 80
        },
        'erlenmeyer.500': {
          glass: 200,
          plastic: 100
        },
        'erlenmeyer.1000': {
          glass: 350,
          plastic: 150
        },
        'erlenmeyer.2000': {
          glass: 600,
          plastic: 350
        },
        'erlenmeyer.5000': {
          glass: 950,
          plastic: 500
        },
        'pipette.2': {
          glass: 15,
          plastic: 4
        },
        'pipette.5': {
          glass: 22,
          plastic: 7
        },
        'pipette.10': {
          glass: 29,
          plastic: 10
        },
        'pipette.25': {
          glass: 46,
          plastic: 15
        }
      },
      selectedCountry: null,
      countries: []
    }
  },
  computed: {
    unit: function () {
      if (this.impact === 'water') {
        return this.$t('litre')
      } else {
        return this.$t('g.co2e')
      }
    },
    langKey: function () {
      if (this.$i18n.locale === 'fr') {
        return 'nameFR'
      } else {
        return 'nameEN'
      }
    },
    filteredCountries: function () {
      return this.countries.filter((option) => {
        return (
          option[this.langKey]
            .toString()
            .toLowerCase()
            .indexOf(this.selectedCountry.toLowerCase()) >= 0
        )
      })
    },
    glassIntensity: function () {
      if (this.labwareName) {
        let intensity = this.glass.getImpact()
        if (isNaN(intensity.intensity[this.impact])) {
          return 0
        } else {
          return intensity.intensity[this.impact]
        }
      } else {
        return 0
      }
    },
    plasticIntensity: function () {
      let intensity = this.plastic.getImpact()
      if (isNaN(intensity.intensity[this.impact])) {
        return 0
      } else {
        return intensity.intensity[this.impact]
      }
    },
    reductionRate: function () {
      if (this.plasticIntensity > 0 && this.glassIntensity > 0) {
        if (this.plasticIntensity > this.glassIntensity) {
          let diff = this.plasticIntensity - this.glassIntensity
          return (-diff * 100) / this.plasticIntensity
        } else {
          let diff = this.glassIntensity - this.plasticIntensity
          return (diff * 100) / this.glassIntensity
        }
      } else {
        return 0
      }
    }
  },
  methods: {
    setCountry (option) {
      if (option) {
        this.glass.country = option.flag
        this.plastic.country = option.flag
      }
    }
  },
  watch: {
    labwareName: {
      handler () {
        this.glass.name = this.labwareName
        this.glass.weight = this.WEIGHTS[this.labwareName].glass
        this.plastic.weight = this.WEIGHTS[this.labwareName].plastic
      }
    }
  },
  mounted () {
    this.countries = Object.keys(LABWARES_FACTORS)
      .filter(
        (country) =>
          country.startsWith('electricity.') &&
          LABWARES_FACTORS[country]['description_FR'] !== ''
      )
      .map((country) => {
        return {
          flag: country.split('.')[1],
          nameFR: LABWARES_FACTORS[country]['description_FR'],
          nameEN: LABWARES_FACTORS[country]['description_EN']
        }
      })
    this.selectedCountry = this.countries.filter(
      (c) => c.flag === this.glass.country
    )[0][this.langKey]
  }
}
</script>

<style>
.notification-icon {
  margin-top: 40px;
  margin-bottom: 5px;
  position: relative;
}
.notification-icon > .bicon {
  position: absolute;
  top: -17px;
  left: 0px;
  border-radius: 50%;
  display: inline-block;
}
.notification-icon > .bicon > span > i {
  font-size: 28px;
  padding-left: 0px;
  color: #000;
  z-index: 2;
}
.notification-icon > .bicon {
  display: inline-block;
  background: hsl(0, 0%, 96%);
  padding: 16px;
  border-radius: 50%;
  transition: 0.3s;
  width: 62px;
  height: 62px;
  color: #fff;
}
.notification-icon > span {
  padding-left: 75px;
  font-weight: bold;
  font-size: 0.9em;
  text-transform: uppercase;
}
</style>
